import * as React from "react";
import { Link } from "react-router";

import {
  snapshotCollectionRoute,
  useGetContractFamilyPipelineKeyTermsQuery,
  useGetContractFamilyPipelineSourceLinkQuery,
} from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";

import { formatPipelineCitationText } from "../pipeline/pipelineCitationText";
import { buildPipelineReturnUrl } from "../pipeline/stageNavigation";

const PDFViewer = React.lazy(async () => ({
  default: (await import("@/components/feature/pdf/pdfViewer")).PDFViewer,
}));

interface Props {
  runId: string;
  documentId: string;
  documentFseId: string | null;
  documentName: string;
  sourcePath: string | null;
}

export const ContractFamilyReferencePanel = ({ runId, documentId, documentFseId, documentName, sourcePath }: Props) => {
  const [view, setView] = React.useState<"document" | "key_terms">("document");
  const { currentCitation, setCurrentCitation, setCurrentPdfId } = useCurrentPdf();
  const terms = useGetContractFamilyPipelineKeyTermsQuery({ runId, documentId }, { skip: view !== "key_terms" });
  const source = useGetContractFamilyPipelineSourceLinkQuery(runId);
  const sourceUrl = source.data
    ? snapshotCollectionRoute(source.data.template_id, source.data.collection_id)
    : undefined;

  React.useEffect(() => {
    setCurrentPdfId(documentFseId);
    setCurrentCitation(null);
  }, [documentFseId, setCurrentCitation, setCurrentPdfId]);

  const navigateToCitation = (fieldId: string, citationIndex: number, page: number) => {
    if (!documentFseId) return;
    setCurrentPdfId(documentFseId);
    setCurrentCitation({
      citationId: `pipeline-${fieldId}-${citationIndex}`,
      page,
      highlights: [],
      activeHighlightIndex: 0,
      fseId: documentFseId,
      documentId,
      citation: citationIndex + 1,
      index: citationIndex,
    });
    setView("document");
  };

  return (
    <aside aria-label={`References for ${documentName}`} className="rounded-md border border-slate-200 p-3">
      <div role="tablist" aria-label="Reference view" className="mb-3 flex gap-2">
        <Button role="tab" aria-selected={view === "document"} variant="outline" onClick={() => setView("document")}>
          Document
        </Button>
        <Button role="tab" aria-selected={view === "key_terms"} variant="outline" onClick={() => setView("key_terms")}>
          Key terms
        </Button>
      </div>
      {view === "document" ? (
        <div>
          <p className="font-medium text-slate-950">{documentName}</p>
          {sourcePath ? <p className="mt-1 break-all text-sm text-slate-600">{sourcePath}</p> : null}
          {sourceUrl ? (
            <a className="mt-3 inline-block text-sm font-medium underline" href={sourceUrl}>
              Open document in source collection
            </a>
          ) : null}
          {documentFseId ? (
            <>
              <Link
                className="ml-3 inline-block text-sm font-medium underline"
                to={`/chat/${encodeURIComponent(documentFseId)}`}
                state={{ fromSnapshot: buildPipelineReturnUrl(runId, "categorise_review_confirm") }}
              >
                Go to Q&amp;A
              </Link>
              <div
                aria-label="PDF viewer"
                className="mt-3 h-[36rem] min-h-80 overflow-hidden rounded border bg-slate-50"
              >
                <React.Suspense
                  fallback={
                    <div role="status" className="flex h-full items-center justify-center text-sm text-slate-500">
                      Loading document…
                    </div>
                  }
                >
                  <PDFViewer citationHighlight={currentCitation} />
                </React.Suspense>
              </div>
            </>
          ) : (
            <p className="mt-3 text-sm text-slate-500">PDF unavailable for this document.</p>
          )}
        </div>
      ) : terms.isLoading ? (
        <p role="status" className="text-sm text-slate-500">
          Loading key terms…
        </p>
      ) : (
        <dl className="space-y-3">
          {(terms.data?.fields ?? []).map(field => (
            <div key={field.field_id}>
              <dt className="text-sm font-medium text-slate-700">{field.name ?? "Key term"}</dt>
              <dd className="whitespace-pre-wrap text-sm text-slate-950">
                {formatPipelineCitationText(field.value, field.citations)}
              </dd>
              {field.citations.length > 0 ? (
                <ul className="mt-1 flex flex-wrap gap-2">
                  {field.citations.map((citation, index) => {
                    const page = citation.page;
                    const canNavigate = documentFseId !== null && page !== null;
                    return (
                      <li key={`${field.field_id}-${index}`}>
                        {canNavigate ? (
                          <button
                            type="button"
                            className="text-xs font-medium underline"
                            onClick={() => navigateToCitation(field.field_id, index, page)}
                          >
                            Page {page}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-500">Source page unavailable</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          ))}
        </dl>
      )}
    </aside>
  );
};
